import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type { TerritoryPointMapping } from "@esWebAPI/es/map/TerritoryPointMapping";

export const SELECTION_TYPE = {
    AREAS_AND_ACCOUNTS: "AREAS_AND_ACCOUNTS",
    ACCOUNTS_ONLY: "ACCOUNTS_ONLY"
};

type State = {
    territoryId: number,
    isSelected: boolean
}

export type FeatureState = {
    featureId: number,
    state:State
};

type ExceptionDef = {
    layerMetaId: number,
    featureId: number,
    territoryId: number
}

export default class TerritorySelectionModel {

    territoryModel: TerritoryModel;
    territoryPointMapping: TerritoryPointMapping;
    baseFeatIds: number[] = [];
    basePointIds: number[] = [];
    upperLevelIds: number[] = [];
    changedDatasetPoints = new Map<string, State>();
    implicitDatasetPoints: {[layerMetaId: number]: number[]} = {}; // Points that are implicitly selected when base features are selected.
    upperLevelBaseCache:{[upperLevelId: number]: unknown} = [];
    eventChannel: any = null;
    mode: keyof typeof SELECTION_TYPE = "AREAS_AND_ACCOUNTS";
    territoryIdsInChangedPoints = {};
    selectionInteractionsCount = 0;

    constructor(territoryModel: TerritoryModel, territoryPointMapping: TerritoryPointMapping, eventChannel: unknown) {
        this.territoryModel = territoryModel;
        this.territoryPointMapping = territoryPointMapping;
        this.eventChannel = eventChannel;
        this.reset();
    }

    reset() {
        const hadPointFeatureSelected = this.changedDatasetPoints.size > 0 || _.flatten(Object.values(this.implicitDatasetPoints)).length > 0;

        const ids = this.getMode() === "BASE" ? this.getBaseFeatIds() : this.getAllBaseFeatsInUpperLevelBaseCache();
        const pointsIds = this.getBasePointIds();
        this.baseFeatIds = [];
        this.basePointIds = [];
        this.upperLevelIds = [];
        this.upperLevelBaseCache = [];
        this.changedDatasetPoints.clear();
        this.implicitDatasetPoints = {};
        this.territoryIdsInChangedPoints = {};
        this.selectionInteractionsCount = 0;
        this.eventChannel.fireEvent("onSelectionChange", ids, pointsIds, hadPointFeatureSelected);
    }

    isActive() {
        return !(_.isEmpty(this.baseFeatIds) && _.isEmpty(this.basePointIds) && _.isEmpty(this.upperLevelIds) && this.changedDatasetPoints.size === 0);
    }

    getBaseFeatIds() {
        return this.baseFeatIds;
    }

    getBasePointIds() {
        return this.basePointIds;
    }

    getUpperLevels() {
        return this.upperLevelIds;
    }

    getEventChannel() {
        return this.eventChannel;
    }

    addBaseFeats(ids: number[] | number, suppressEvents = false) {
        this.selectionInteractionsCount++;
        ids = Array.isArray(ids) ? ids : [ids];

        if(_.isEmpty(ids)){
            console.debug("addBaseFeats called with empty ids", ids);
            return;
        }

        if(this.upperLevelIds.length > 0){
            //throw new Error("");
            console.error("Cannot addBasePoints as there are already upperLevel territories selected", this.toJSON());
        }

        const baseFeatureIdsAdded = _.difference(ids, this.baseFeatIds);
        if(baseFeatureIdsAdded.length === 0) {
            return;
        }

        // If we have filtered out points on the Base Features, we will add such points in the changedDatasetPoints
        // as NOT selected, so we don't reassign them. See TOR9-18164
        const filteredInTerritoryIds = new Set(this.territoryModel.getFilter());
        const hasFilter = filteredInTerritoryIds.size > 0;
        for (const baseFeatureId of baseFeatureIdsAdded) {
            const nodesForBaseFeature = this.territoryPointMapping.baseFeatureIdMapping.get(baseFeatureId);
            if (!nodesForBaseFeature) {
                continue;
            }

            for (const [layerMetaId, featureMapping] of nodesForBaseFeature.entries()) {
                const pointStates = [];
                for (const node of featureMapping.values()) {
                    const addToChangedDatasetPoints = !node.isVisible && (!hasFilter || filteredInTerritoryIds.has(node.territoryId));
                    if (!addToChangedDatasetPoints) {
                        continue;
                    }

                    pointStates.push({
                        featureId: node.featureId,
                        state: {
                            territoryId: node.territoryId,
                            isSelected: false
                        }
                    })
                }
                this.toggleDatasetPointsSelection(layerMetaId, pointStates, true);
            }

        }

        this.baseFeatIds = this.baseFeatIds.concat(baseFeatureIdsAdded);
        if (suppressEvents !== true) {
            this.eventChannel.fireEvent("onSelectionChange", ids);
        } else {
            es.log.debug("This will not trigger the redraw of the hatching on the map")
        }
    }

    addImplicitPointFeatures(layerMetaId: number, featureIds: number[], ignoreIsException: boolean, suppressEvents = false) {

        this.selectionInteractionsCount++;

        if (!this.implicitDatasetPoints.hasOwnProperty(layerMetaId)){
            this.implicitDatasetPoints[layerMetaId] = [];
        }

        //Filter out features assigned as exceptions
        if (!ignoreIsException) {
            featureIds = featureIds.filter((featureId) => {
                const isFeatureSelected = this.changedDatasetPoints.get(`${layerMetaId}_${featureId}`)?.isSelected ?? true;
                return !this.territoryPointMapping.getNode(layerMetaId, featureId).isException && isFeatureSelected;
            });
        }

        this.implicitDatasetPoints[layerMetaId] = _.union(this.implicitDatasetPoints[layerMetaId], featureIds)
            .sort((a, b) => {
                return a - b
            });

        if(suppressEvents !== true) {
            this.eventChannel.fireEvent("onSelectionChange", this.baseFeatIds, [], true);
        }
    }

    fireChangeEvent() {
        const baseFeatureIds = this.baseFeatIds.length > 0 ? this.baseFeatIds : this.upperLevelBaseCache.flatMap(territory => territory.baseFeatIds);
        const basePointFeatureIds = this.basePointIds;
        this.eventChannel.fireEvent("onSelectionChange",baseFeatureIds, basePointFeatureIds, true)
    }

    removeImplicitPointFeatures(layerMetaId: number, featureIds: number[], baseFeatureIds: number[], suppressEvents=false) {

        this.selectionInteractionsCount++;
        if (this.implicitDatasetPoints.hasOwnProperty(layerMetaId)){
            this.implicitDatasetPoints[layerMetaId] = _.difference(this.implicitDatasetPoints[layerMetaId], featureIds);
        }

        if (suppressEvents !== true) {
            this.eventChannel.fireEvent("onSelectionChange", baseFeatureIds, [], true);
        }
    }
    addBasePoints(ids: number|number[], suppressEvents= false) {
        this.selectionInteractionsCount++;
        ids = Array.isArray(ids) ? ids : [ids];

        if(_.isEmpty(ids)){
            console.debug("addBasePoints called with empty ids", ids);
            return;
        }

        if(this.upperLevelIds.length > 0){
            //throw new Error("");
            console.error("Cannot addBasePoints as there are already upperLevel territories selected", this.toJSON());
        }

        this.basePointIds = _.union(this.basePointIds, ids);
        if(suppressEvents !== true){
            this.eventChannel.fireEvent("onSelectionChange", ids);
        }
    }

    /**
     * Add upperlevel territories.
     * Note: the base and point feats associated with the ter must be added for caching during rendering.
     * @param upperLevelTers {territoryId: 1, baseFeatIds: [], basePointIds: [], exceptions: []}
     * @param suppressEvents to suppress onSelectionChange event being fired
     */
    addUpperLevels(upperLevelTers, suppressEvents = false) {
        this.selectionInteractionsCount++;
        upperLevelTers = Array.isArray(upperLevelTers) ? upperLevelTers : [upperLevelTers];

        console.debug("addUpperLevels", upperLevelTers);

        const baseFeatIdsSelected = [];
        const territoryIdSet = new Set(this.upperLevelIds);

        for (const upperLevelTer of upperLevelTers) {
            if (!territoryIdSet.has(upperLevelTer.territoryId)) {
                this.upperLevelIds.push(upperLevelTer.territoryId); // Keep pushing to array for future needs
                territoryIdSet.add(upperLevelTer.territoryId); // Update Set for fast lookups

                if (!this.territoryModel.isAccountOnly()) {
                    if (upperLevelTer.baseFeatIds.length || upperLevelTer.basePointIds.length) {
                        baseFeatIdsSelected.push(...upperLevelTer.baseFeatIds); // Avoid concat
                        this.upperLevelBaseCache.push(es.util.object.deepCopy(upperLevelTer));
                    }
                }
                // Select implicitly all accounts of that territory
                for (const exception of upperLevelTer?.exceptions || []) {
                    this.addImplicitPointFeatures(exception.layerMetaId, exception.featureIds, true, true);
                }
            }
        }

        if (!suppressEvents) {
            const pointsSelected = this.territoryModel.isAccountOnly();
            this.eventChannel.fireEvent("onSelectionChange", baseFeatIdsSelected, [], pointsSelected);
        } else {
            es.log.debug("This will not trigger the redraw of the hatching on the map");
        }
    }


    removeBaseFeats(ids: number[]|number, suppressEvents = false) {
        this.selectionInteractionsCount++;
        ids = Array.isArray(ids) ? ids : [ids];
        this.baseFeatIds = _.differenceBy(this.baseFeatIds, ids);
        if(suppressEvents !== true){
            this.eventChannel.fireEvent("onSelectionChange", ids);
        } else {
            es.log.debug("This will not trigger the redraw of the hatching on the map");
        }
    }

    getAllBaseFeatsInUpperLevelBaseCache() {
        const baseFeatIdsSelected: number[] = [];
        for (const ter of this.upperLevelBaseCache) {
            baseFeatIdsSelected.push(...ter.baseFeatIds);
        }
        es.log.log("getAllBaseFeatsInUpperLevelBaseCache : count", baseFeatIdsSelected.length);
        return baseFeatIdsSelected;
    }

    removeBasePoints(ids: number[]|number, suppressEvents = false) {
        this.selectionInteractionsCount++;
        ids = Array.isArray(ids) ? ids : [ids];
        this.basePointIds = _.differenceBy(this.basePointIds, ids);
        if(suppressEvents !== true){
            this.eventChannel.fireEvent("onSelectionChange", ids);
        }
    }

    removeUpperLevels(ids:number[]|number, suppressEvents=false) {
        this.selectionInteractionsCount++;
        ids = Array.isArray(ids) ? ids : [ids];
        for (const id of ids) {
            for (let j = this.upperLevelBaseCache.length - 1; j > -1; j--) {
                if (id == this.upperLevelBaseCache[j].territoryId) {
                    this.upperLevelBaseCache.splice(j, 1);
                    break;
                }
            }

            const territoryExceptions = this.territoryModel.getExceptions(id)
            for (const exception of territoryExceptions) {
                this.removeImplicitPointFeatures(exception.layerMetaId, exception.featureIds, [], true);
            }
        }

        this.upperLevelIds = _.differenceBy(this.upperLevelIds, ids);

        if(suppressEvents !== true){
            const pointsSelected = this.territoryModel.isAccountOnly();
            this.eventChannel.fireEvent("onSelectionChange", ids, [], pointsSelected);
        }
    }

    toggleDatasetPointSelection(layerMetaId: number, featureId: number, state: State, suppressEvents=false) {
        this.selectionInteractionsCount++;
        const key = layerMetaId + "_" + featureId;
        this.changedDatasetPoints.set(key, state);
        this.territoryIdsInChangedPoints[state.territoryId] = true;

        if (this.implicitDatasetPoints[layerMetaId]) {
            let index = es.util.array.binarySearch(this.implicitDatasetPoints[layerMetaId], featureId);
            if(index > -1){
                this.implicitDatasetPoints[layerMetaId].splice(index, 1);
            }
        }

        if(suppressEvents !== true){
            this.eventChannel.fireEvent("onSelectionChange", [], [], true);
        }
    }

    unselectDatasetPoints(layerMetaId: number, featureIds: number[], suppressEvents=false) {

        this.selectionInteractionsCount++;
        for(const featureId of featureIds) {

            const key = layerMetaId + "_" + featureId;
            const state = this.changedDatasetPoints.get(key);

            if(state) {
               state.isSelected = false
                this.changedDatasetPoints.set(key, state);
            }
        }

        if(suppressEvents !== true){
            this.eventChannel.fireEvent("onSelectionChange", [], [], true);
        }
    }


    toggleDatasetPointsSelection(layerMetaId: number, featuresStateArray: FeatureState[], suppressEvents=false) {

        if(featuresStateArray.length === 0){
            return;
        }

        for (let i = 0; i < featuresStateArray.length-1; i++) {
            const {featureId, state} = featuresStateArray[i];
            this.toggleDatasetPointSelection(layerMetaId, featureId, state, true);
        }

        //On the last toggle selection send the event
        const {featureId, state} = featuresStateArray[featuresStateArray.length-1];
        this.toggleDatasetPointSelection(layerMetaId, featureId, state, suppressEvents);
    }


    isPointFeatureSelected(layerMetaId: number, featureId: number) {
        const key = layerMetaId + "_" + featureId;
        if (this.changedDatasetPoints.get(key)?.isSelected) {
            return true;
        }

        return this.implicitDatasetPoints.hasOwnProperty(layerMetaId) &&
        es.util.array.binarySearch(this.implicitDatasetPoints[layerMetaId], featureId) > -1;
    }

    computeAndGetExceptions(targetTerritoryId: number) {

        const exceptionsToAdd:ExceptionDef [] = []
        const exceptionsToRemove:ExceptionDef[] = []

        this.changedDatasetPoints.forEach((state, key) =>{
           const [layerMetaId_, featureId_] = key.split("_");
           const featureId = +featureId_;
           const layerMetaId = +layerMetaId_;

           const node = this.territoryPointMapping.getNode(layerMetaId, featureId);
           if(!node) {
               console.warn("There is a node in the selection that doesn't exist in the point mapping", layerMetaId, featureId);
               return true;
           }
            const isException = node.isException ?? false;
            const baseFeatureId = node.baseFeatureId;
            const pointMappingTerritoryId = node.territoryId;

            const isBaseFeatureSelectedForPoint = !this.territoryModel.isAccountOnly() &&
                baseFeatureId !== undefined && this.baseFeatIds.includes(baseFeatureId);

            if(state.isSelected && !isBaseFeatureSelectedForPoint){
                exceptionsToAdd.push({layerMetaId, featureId, territoryId: targetTerritoryId});
            } else if(!state.isSelected && isBaseFeatureSelectedForPoint){
                exceptionsToAdd.push({layerMetaId, featureId, territoryId: pointMappingTerritoryId});
            } else if(state.isSelected && isBaseFeatureSelectedForPoint && isException){
                exceptionsToRemove.push({layerMetaId, featureId, territoryId: state.territoryId});
            }
        });

        const exceptionsToAddByTerritoryId = _.groupBy(exceptionsToAdd, (e) => e.territoryId);

        const groupByLayerMetaId = function(exceptionsPerTerritory: {[territoryId: number]: ExceptionDef}) {

            const exceptions = []
                const exceptionsPerLayerMetaId = _.groupBy(exceptionsPerTerritory, e => e.layerMetaId);
                for (const [layerMetaId, e] of Object.entries(exceptionsPerLayerMetaId)) {
                    exceptions.push({
                        layerMetaId: +layerMetaId,
                        featureIds: e.map(e => +e.featureId)
                    });
                }
           return exceptions;
        }

        const _exceptionsToAdd = _.map(exceptionsToAddByTerritoryId, (ex, territoryId) => {
         return {
             territoryId,
             exceptions: groupByLayerMetaId(ex)
         }
        });

        return [_exceptionsToAdd, groupByLayerMetaId(exceptionsToRemove)];
    }

    /**
     * TO BE USED EXCLUSIVELY FOR ACCOUNTS-ONLY TERRITORIES
     * Generates the ES_EXCEPTIONS array for each territoryId with the points in the selected state as the first return value
     * And the territoryIds in the selection model as the second one
     * [{territoryId : [{layerMetaId: [featureIds]}]}, {territoryId: true}]
     * @returns {{}[obj]}
     */
    accountOnly_getExceptionsMappedByTerritoryId() {

        if (!this.territoryModel.isAccountOnly()) {
            console.error("Only use this for Account-Only territories");
            return;
        }

        const exceptions:ExceptionDef[] = [];
        const territoryIdsObj = {};

        this.changedDatasetPoints.forEach((state, key) =>{
            const [layerMetaId, featureId_] = key.split("_");
            const featureId = +featureId_;

            if(state.isSelected){
                exceptions.push({layerMetaId, featureId, territoryId:state.territoryId});
            }
            territoryIdsObj[state.territoryId] = true;
        });


        const exceptionsToAddByTerritoryId = _.groupBy(exceptions, (e) => e.territoryId);

        const groupByLayerMetaId = function(exceptionsPerTerritory: {[territoryId: number]: ExceptionDef}) {

            const exceptions = []
            const exceptionsPerLayerMetaId = _.groupBy(exceptionsPerTerritory, e => e.layerMetaId);
            for (const [layerMetaId, e] of Object.entries(exceptionsPerLayerMetaId)) {
                exceptions.push({
                    layerMetaId: +layerMetaId,
                    featureIds: e.map(e => +e.featureId)
                });
            }
            return exceptions;
        }

        const exceptionsPerTerritoryId = {};

        _.each(exceptionsToAddByTerritoryId, (exceptions, territoryId) => {

            exceptionsPerTerritoryId[territoryId] = groupByLayerMetaId(exceptions);
        });

        return [exceptionsPerTerritoryId, territoryIdsObj];
    }

    isExceptionForTerritory(layerMetaId: number, featureId: number, territoryId: number) {
        const datasetPointState = this.changedDatasetPoints.get(layerMetaId + "_" + featureId);

        if(datasetPointState === undefined){
            return false;
        }

        return datasetPointState.territoryId === territoryId;
    }

    getChangedPoints() {
        return this.changedDatasetPoints;
    }

    hasSelectedChangedPoints(territoryId: number) {

        return [...this.changedDatasetPoints.values()].findIndex(ex => ex.territoryId === territoryId && ex.isSelected) !== -1;
    }

    getChangedPointsForTerritoryId(territoryId: number) {

        const kv: {[layerMetaId: string]: {[featureId: string]: State}} = {};
        this.changedDatasetPoints.forEach((value, key) => {
            const [layerMetaId, featureId] = key.split("_");
            if (value.territoryId === territoryId) {

                if(kv[layerMetaId] === undefined) {
                    kv[layerMetaId] = {}
                }
                kv[layerMetaId][featureId] = value;
            }
        });
        return kv;
    }

    getChangedPointsPerTerritoryId(){
        const territories = {};
        this.changedDatasetPoints.forEach((state, key) => {
            const [layerMetaId, featureId] = key.split("_");
            const territoryId = state.territoryId;

            if ( territories[territoryId] === undefined) {
                territories[territoryId] = {isSelected: false};
            }

            if(territories[territoryId][layerMetaId] === undefined) {
                territories[territoryId][layerMetaId] = {};
            }

            territories[territoryId][layerMetaId][featureId] = state;
            territories[territoryId].isSelected = territories[territoryId].isSelected || state.isSelected;
        });
        return territories;
    }

    getMode() {
        if (this.baseFeatIds.length || this.basePointIds.length
            || this.changedDatasetPoints.size !== 0
        ) {
            return "BASE";
        } else if (this.upperLevelIds.length) {
            return "UPPER";
        } else {
            return "NONE";
        }
    }

    isAllBaseSelected(ids: number[] | number) {
        ids = Array.isArray(ids) ? ids : [ids];
        return es.util.array.includesAll(this.baseFeatIds, ids);
    }

    isAllBasePointSelected(ids: number[] | number) {
        ids = Array.isArray(ids) ? ids : [ids];
        return es.util.array.includesAll(this.basePointIds, ids);
    }

    isAllUpperSelected(ids: number[] | number) {
        ids = Array.isArray(ids) ? ids : [ids];
        return es.util.array.includesAll(this.upperLevelIds, ids);
    }

    isAnyUpperSelected(ids: number[] | number) {
        ids = Array.isArray(ids) ? ids : [ids];
        return this.upperLevelIds.some(r => ids.includes(r));
    }

    toJSON() {
        // clone?
        return {
            baseFeatIds: this.baseFeatIds,
            basePointIds: this.basePointIds,
            changedDatasetPoints: this.changedDatasetPoints,
            upperLevelIds: this.upperLevelIds,
            upperLevelBaseCache: this.upperLevelBaseCache,
            selectionInteractionsCount: this.selectionInteractionsCount
        };
    }

    setSelectionType(newMode: keyof typeof SELECTION_TYPE) {
        this.mode = newMode;
    }

    getSelectionType(){
        return this.mode;
    }
}